import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getItemsPaginated, logout as authLogout } from "../services/dataService";
import api from "../utils/api";
import Sidebar from "./Sidebar";
import WorkspaceHeader from "./WorkspaceHeader";
import CurrencyInput from "./CurrencyInput";
import { playSaveSuccess } from "../utils/sounds";
import { useToast } from "../contexts/ToastContext";
import { getInventoryBarcodeDisplay } from "../utils/inventoryPresentation";
import {
  buildQuickEditUnitsForSave,
  getQuickEditUnits,
} from "../utils/quickEditUnits";
import { getQuickEditSaveMode } from "../utils/quickEditPermissions";

const PAGE_SIZE = 24;
const QUICK_EDIT_PAGE_STORAGE_KEY = "quickEditPage";

const getSavedPage = () => {
  const savedPage = parseInt(localStorage.getItem(QUICK_EDIT_PAGE_STORAGE_KEY), 10);
  return Number.isInteger(savedPage) && savedPage >= 1 ? savedPage : 1;
};

function QuickEdit() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(getSavedPage);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mobileMenuButtonRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pageDraft, setPageDraft] = useState(() => String(getSavedPage()));
  const skipPageBlur = useRef(false);
  const searchTimer = useRef(null);
  const currentSearch = useRef("");
  const initialFetchStarted = useRef(false);
  const totalsBySearch = useRef(new Map());
  const showToast = useToast();

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
    if (initialFetchStarted.current) return;
    initialFetchStarted.current = true;
    fetchPage(page, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageDraft(String(page));
    localStorage.setItem(QUICK_EDIT_PAGE_STORAGE_KEY, String(page));
  }, [page]);

  const fetchPage = async (p, q) => {
    setLoading(true);
    try {
      const searchKey = q.trim();
      const cachedTotal = totalsBySearch.current.get(searchKey);
      const { items: data, total: count } = await getItemsPaginated({
        page: p,
        pageSize: PAGE_SIZE,
        search: q,
        includeCount: cachedTotal === undefined,
      });
      setItems(data);
      if (count !== null) {
        totalsBySearch.current.set(searchKey, count);
        setTotal(count);
      } else {
        setTotal(cachedTotal);
      }
      setPage(p);
    } catch {
      showToast("Failed to load items", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    currentSearch.current = q;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchPage(1, q), 300);
  };

  const getEdit = (item, field) => edits[item.id]?.[field] ?? item[field] ?? "";

  const handleChange = (item, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [item.id]: { ...prev[item.id], [field]: value },
    }));
  };

  const handleSave = async (item) => {
    if (!edits[item.id]) return;
    const saveMode = getQuickEditSaveMode(user?.role, edits[item.id]);
    if (saveMode === "forbidden") {
      showToast("Only administrators can change prices. Stock was not saved.", "error");
      return;
    }

    setSaving((prev) => ({ ...prev, [item.id]: true }));
    try {
      const nextPrice = parseFloat(getEdit(item, "price"));
      const nextStock = parseInt(getEdit(item, "stock")) || 0;
      const nextUnits = buildQuickEditUnitsForSave(
        item.product_units,
        nextPrice,
      );
      if (saveMode === "stock") {
        await api.post("/items/stock", {
          itemId: item.id,
          barcode: item.barcode,
          stock: nextStock,
        });
      } else {
        await api.put(`/items/${item.id}`, {
          barcode: item.barcode,
          name: item.name,
          price: nextPrice,
          base_price: parseFloat(getEdit(item, "base_price")) || 0,
          category: item.category,
          stock: nextStock,
          ...(nextUnits.length > 0 ? { units: nextUnits } : {}),
        });
      }
      setItems((prev) =>
        prev.map((i) =>
          i.id !== item.id ? i : {
            ...i,
            price: nextPrice,
            base_price: parseFloat(getEdit(item, "base_price")) || 0,
            stock: nextStock,
            product_units: buildQuickEditUnitsForSave(
              i.product_units,
              nextPrice,
            ),
          }
        )
      );
      setEdits((prev) => { const n = { ...prev }; delete n[item.id]; return n; });
      playSaveSuccess();
      showToast(`${item.name} updated`, "success");
    } catch (err) {
      showToast(err.response?.data?.error || "Failed to save", "error");
    } finally {
      setSaving((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isDirty = (item) => !!edits[item.id];
  const isCashier = user?.role === "cashier";

  const getValidPage = (value, fallback = page) => {
    const parsed = parseInt(value);
    return !isNaN(parsed) && parsed >= 1 && parsed <= totalPages ? parsed : fallback;
  };

  const navigatePage = (offset = 0) => {
    const basePage = getValidPage(pageDraft);
    const target = Math.min(totalPages, Math.max(1, basePage + offset));
    setPageDraft(String(target));
    fetchPage(target, currentSearch.current);
  };

  const fieldStyle = {
    width: "100%",
    padding: "6px 8px",
    background: "var(--clr-bg)",
    border: "1px solid var(--clr-border)",
    borderRadius: "var(--radius)",
    color: "var(--clr-text)",
    fontSize: "var(--text-sm)",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "11px",
    color: "var(--clr-muted)",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4,
    display: "block",
  };

  return (
    <div className="app-layout">
      <Sidebar
        user={user}
        onLogout={async () => {
          await authLogout();
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
        }}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
        menuButtonRef={mobileMenuButtonRef}
      />
      <main className={`main-content ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        <button
          ref={mobileMenuButtonRef}
          className="mobile-menu-btn"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
          aria-controls="app-sidebar"
          aria-expanded={sidebarOpen}
        >
          <span></span>
        </button>

        <WorkspaceHeader
          workspaceLabel="Cashier"
          pageLabel="Quick Edit"
          stationLabel={user?.role === "admin" ? "Back office" : "Front counter"}
          user={user}
          onOpenCashier={user?.role === "admin" ? () => navigate("/cashier") : undefined}
        />

        <div style={{ padding: "var(--sp-6)" }}>
          <h1 style={{ fontSize: "var(--text-2xl)", fontWeight: 700, marginBottom: "var(--sp-1)" }}>
            Quick Edit
          </h1>
          <p style={{ color: "var(--clr-muted)", marginBottom: "var(--sp-5)" }}>
            Edit sell price, base price, and stock directly.
          </p>

          <div className="quick-edit-toolbar">
            <input
              type="text"
              className="scan-input quick-edit-search"
              placeholder="Search by name or barcode…"
              value={search}
              onChange={handleSearchChange}
            />

            {!loading && total > 0 && (
              <div className="quick-edit-pagination-summary">
                {totalPages > 1 && (
                  <div className="quick-edit-pagination-controls">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onPointerDown={() => { skipPageBlur.current = true; }}
                      onClick={() => {
                        skipPageBlur.current = false;
                        navigatePage(-1);
                      }}
                      disabled={getValidPage(pageDraft) === 1}
                    >
                      Previous
                    </button>
                    <div className="d-flex align-center gap-2">
                      <span className="pagination-info">Page</span>
                      <input
                        key={`quick-edit-page-${page}`}
                        type="number"
                        min="1"
                        max={totalPages}
                        value={pageDraft}
                        aria-label="Current page"
                        className="quick-edit-page-jump"
                        onChange={(e) => setPageDraft(e.target.value)}
                        onBlur={(e) => {
                          if (skipPageBlur.current) {
                            skipPageBlur.current = false;
                            return;
                          }
                          const target = getValidPage(e.target.value, null);
                          if (target === null) setPageDraft(String(page));
                          else navigatePage(0);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const target = getValidPage(pageDraft, null);
                            if (target === null) setPageDraft(String(page));
                            else navigatePage(0);
                          }
                        }}
                      />
                      <span className="pagination-info">of {totalPages}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onPointerDown={() => { skipPageBlur.current = true; }}
                      onClick={() => {
                        skipPageBlur.current = false;
                        navigatePage(1);
                      }}
                      disabled={getValidPage(pageDraft) === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}

                <div className="quick-edit-result-count">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} items
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: "center", color: "var(--clr-muted)", padding: "var(--sp-10) 0" }}>
              Loading…
            </div>
          ) : (
            <>
              <div className="quick-edit-grid">
                {items.map((item) => {
                  const dirty = isDirty(item);
                  const packagingUnits = getQuickEditUnits(item);
                  const barcodeDisplay = getInventoryBarcodeDisplay(item, search);
                  return (
                    <div
                      key={item.id}
                      style={{
                        background: "var(--clr-surface)",
                        border: `1.5px solid ${dirty ? "var(--clr-accent)" : "var(--clr-border)"}`,
                        borderRadius: "var(--radius-lg)",
                        padding: "var(--sp-4)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--sp-3)",
                        transition: "border-color 0.15s",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "var(--text-base)", lineHeight: 1.3, marginBottom: 4 }}>
                          {item.name}
                        </div>
                        {barcodeDisplay.primary && (
                          <div style={{ fontFamily: "monospace", fontSize: "var(--text-xs)", color: "var(--clr-muted)" }}>
                            {barcodeDisplay.primary}
                            {barcodeDisplay.additionalCount > 0 &&
                              ` +${barcodeDisplay.additionalCount} unit barcodes`}
                          </div>
                        )}
                      </div>

                      <div className="quick-edit-value-grid">
                        <div>
                          <label htmlFor={`quick-edit-${item.id}-sell`} style={labelStyle}>Sell</label>
                          <CurrencyInput id={`quick-edit-${item.id}-sell`} className="numeric-entry quick-edit-currency" min={0} step={50} value={getEdit(item, "price")}
                            onChange={(value) => handleChange(item, "price", value)} label="sell price"
                            disabled={isCashier} title={isCashier ? "Only administrators can change prices" : undefined} />
                        </div>
                        <div>
                          <label htmlFor={`quick-edit-${item.id}-base`} style={labelStyle}>Base</label>
                          <CurrencyInput id={`quick-edit-${item.id}-base`} className="numeric-entry quick-edit-currency" min={0} step={50} value={getEdit(item, "base_price")}
                            onChange={(value) => handleChange(item, "base_price", value)} label="base price"
                            disabled={isCashier} title={isCashier ? "Only administrators can change prices" : undefined} />
                        </div>
                        <div>
                          <label htmlFor={`quick-edit-${item.id}-stock`} style={labelStyle}>Stock</label>
                          <input id={`quick-edit-${item.id}-stock`} type="number" className="numeric-entry quick-edit-stock-input" min="0" step="1" value={getEdit(item, "stock")}
                            onChange={(e) => handleChange(item, "stock", e.target.value)} style={fieldStyle} />
                        </div>
                      </div>

                      {packagingUnits.length > 0 && (
                        <div className="quick-edit-units" aria-label={`Packaging units for ${item.name}`}>
                          <div className="quick-edit-units-title">Packaging units</div>
                          {packagingUnits.map((unit) => (
                            <div className="quick-edit-unit-row" key={unit.id}>
                              <div className="quick-edit-unit-identity">
                                <span className="quick-edit-unit-name">
                                  {unit.unit_name}
                                  {unit.is_base_unit && (
                                    <span className="quick-edit-base-badge">Base</span>
                                  )}
                                </span>
                                <span className="quick-edit-unit-barcode">
                                  {unit.barcode || "No barcode"}
                                </span>
                              </div>
                              <div className="quick-edit-unit-meta">
                                <span>×{unit.conversion_factor}</span>
                                <strong>MMK {Number(unit.selling_price || 0).toLocaleString()}</strong>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        className={`quick-edit-save-btn ${dirty ? "is-ready" : ""}`}
                        onClick={() => handleSave(item)}
                        disabled={!dirty || saving[item.id]}
                        style={{
                          width: "100%",
                          padding: "var(--sp-2) 0",
                          borderRadius: "var(--radius-md)",
                          border: "none",
                          fontWeight: 700,
                          fontSize: "var(--text-sm)",
                        }}
                      >
                        {saving[item.id]
                          ? "Saving…"
                          : dirty
                            ? isCashier ? "Save Stock" : "Save Changes"
                            : "No Changes"}
                      </button>
                    </div>
                  );
                })}
              </div>

              {items.length === 0 && (
                <div style={{ textAlign: "center", color: "var(--clr-muted)", paddingTop: "var(--sp-10)" }}>
                  No items found.
                </div>
              )}

            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default QuickEdit;
